import { atcb_action } from 'add-to-calendar-button';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { kebabCase } from 'lodash/fp';
import { event as analyticEvent, track } from 'modules/Analytics';
import { type Event } from 'modules/App/models/Event';
import { gql, useQuery } from 'modules/GraphQL';
import { getColorAndLabel } from 'modules/Marketing/components/ToolBadge';
import useUser from 'modules/User/hooks/useUser';
import dynamic from 'next/dynamic';
import React from 'react';
import Marquee from 'react-fast-marquee';
import { MdCalendarViewMonth } from 'react-icons/md';
import { useRouter } from 'next/router';
const Calendar = dynamic(() => import('modules/common/components/Calendar'), { ssr: false });
type EventCalendarProps = React.HTMLAttributes<HTMLDivElement> & {
  // TODO
};

import timezone from 'dayjs/plugin/timezone'; // dependent on utc plugin
import utc from 'dayjs/plugin/utc';
import { formatMinutesToHoursAndMinutes } from 'utils/misc';
import useParticipant from '../hooks/useParticipant';
import { EventCard } from '@/lib/components/ui/event-card';
import { type EventParticipant } from '../models/EventParticipant';
import Modal from 'modules/common/components/Modal';
import Button from 'modules/common/components/Button';
import PlanBadge from 'modules/Marketing/components/PlanBadge';
import { Link } from 'modules/i18n';
import { useQueryState } from 'nuqs';
import { Loading } from '@/lib/components/ui/loading';

dayjs.extend(utc);
dayjs.extend(timezone);

export const EVENT_FIELDS = gql`
  fragment AdminEventFields on Event {
    _id
    tool {
      name
      author
      authorUrl
      description
      duration
      type
      price
      priceId
      priceType
      permissions
      url
    }
    participants {
      _id
      userId
      status
      createdAt
    }
    duration
    startAt
    endAt

    name
    url
    author
    authorUrl
    description
  }
`;

export const LIST_EVENTS = gql`
  query EventCalendar {
    events(sort: "-updatedAt") {
      pageInfo {
        count
        page
        limit
      }
      items {
        ...AdminEventFields
      }
    }
  }
  ${EVENT_FIELDS}
`;

const EventInModal = ({ event, onStatusChange }: { event: Event; onStatusChange: (status: string) => Promise<void> }) => {
  const { hasPermission } = useUser();
  const { user } = useUser();
  const router = useRouter();
  if (!event) return null;
  const { startAt: start, endAt: end, tool } = event;

  const name = event.name || tool.name;
  const duration = event.duration || tool.duration || 0;
  const author = event.author || tool.author;
  const authorUrl = event.authorUrl || tool.authorUrl;
  const description = event.description || tool.description;
  const nbMaxParticipants = event.nbMaxParticipants || tool.nbMaxParticipants;
  const url = event.url || tool.url;
  const permissionsLevel = tool.permissions;

  const myParticipation = event.participants?.find((participant: any) => participant.userId === user?.id);
  const status = myParticipation?.status;
  const isPast = dayjs(start).isBefore(dayjs());
  const isFinished = dayjs(end).isBefore(dayjs());

  // Check if this is a paid event requiring permission that user doesn't have
  const canSubscribe = !permissionsLevel || (permissionsLevel && hasPermission(permissionsLevel));
  const canAccessUrl = status === 'registered' || canSubscribe;

  const handleStatusChange = async (newStatus: string) => {
    if (!canSubscribe) {
      const planName = ['booster', 'plus'].includes(permissionsLevel || '')
        ? 'Plus'
        : ['vip', 'teambooster'].includes(permissionsLevel || '')
        ? 'VIP'
        : ['pro', 'ultim'].includes(permissionsLevel || '')
        ? 'Ultim'
        : 'payant';
      const message = `Ton plan ne te permet pas de participer. Passe au plan ${planName} pour accéder à cet événement`;
      await router.push(`/user/subscription?notify=info:${encodeURIComponent(message)}`);
      alert(message);
      return;
    }
    // Otherwise, proceed with normal status change
    await onStatusChange(newStatus);
  };
  const readOnly = !hasPermission?.('member');

  return (
    <EventCard
      className="pb-5 px-5 text-left"
      title={name}
      date={dayjs(start).format('dddd ll')}
      time={`${dayjs(start).format('HH[h]mm')} ${end && dayjs(end).format('HH[h]mm')}`}
      author={{
        name: author,
        url: authorUrl,
        // avatar: '/placeholder.svg?height=32&width=32',
      }}
      initialStatus={status}
      participants={event.participants?.length || 0}
      maxParticipants={nbMaxParticipants || 0}
      duration={duration}
      description={description}
      onStatusChange={handleStatusChange}
      isFinished={isFinished}
      readOnly={readOnly}
    >
      <footer>
        <div className="flex justify-end py-2 flex-col items-end leading-3">
          {/* <span
            className={classNames('font-bold', {
              'line-through cursor-not-allowed': price > 0 && permissionsLevel && hasPermission(permissionsLevel || ''),
            })}
          >
            {price === 0 ? 'Gratuit' : <Price value={price} currency="EUR" className="text-lg font-bold"></Price>}
          </span> */}
          {permissionsLevel && (
            <span className="flex items-center gap-2 text-xs">
              <span className="italic">Réservé aux membres</span>
              <Link href="/pricing">
                <PlanBadge type={permissionsLevel as any} size="sm" />
              </Link>
            </span>
          )}
        </div>
        {!readOnly && (
          <div className="flex gap-2 justify-end py-2">
            {!isPast && (
              <button
                className="btn btn-white w-[280px] sm:w-auto"
                disabled={!canAccessUrl}
                onClick={() => {
                  if (!canAccessUrl) return;
                  analyticEvent({ action: `event-add-to-calendar-${event._id}`, category: 'calendar', label: event._id });
                  return atcb_action({
                    name,
                    description: description || tool.description || '',
                    startDate: dayjs(start).format('YYYY-MM-DD'),
                    startTime: dayjs(start).format('HH:mm'),
                    endTime: dayjs(end).format('HH:mm'),
                    options: ['Apple', 'Google', 'iCal', 'Microsoft365', 'MicrosoftTeams', 'Outlook.com', 'Yahoo'],
                    forceOverlay: true,
                    language: 'fr',
                    timeZone: 'Europe/Paris',
                    iCalFileName: kebabCase(name || ''),
                    location: event.url || window.location.href,
                  });
                }}
              >
                <MdCalendarViewMonth />
                Ajouter au calendrier
              </button>
            )}
            {canAccessUrl && (
              <Button className="btn-primary" href={url} disabled={isFinished}>
                Accéder à l’évènement
              </Button>
            )}
          </div>
        )}
      </footer>
    </EventCard>
  );
};

const EventCalendar: React.FC<EventCalendarProps> = ({ className, ...props }) => {
  const { data, refetch, loading } = useQuery(LIST_EVENTS, { variables: {} });
  const { participate, update: updateParticipation } = useParticipant();
  const { user } = useUser();
  const [from, setFrom] = useQueryState('from');
  const [queryParamEventId] = useQueryState('id');
  const [eventId, setEventId] = React.useState<string | null>(queryParamEventId);

  const { items = [] } = data?.events || {};
  const events = items as Event[];

  const selectedEvent = events.find((event) => event._id === eventId);
  const isParticipating = selectedEvent?.participants?.some(
    (participant: any) => participant.userId === user?.id && participant.status !== 'pending_payment'
  );
  const myParticipation = selectedEvent?.participants?.find((participant: any) => participant.userId === user?.id);

  React.useEffect(() => {
    if (from && selectedEvent) {
      track(`event.viewed.from.${from}`, {
        eventId: selectedEvent._id,
        eventName: selectedEvent.name || selectedEvent.tool.name,
        date: selectedEvent.startAt,
      });
      setFrom(null);
    }
  }, [selectedEvent, from, setFrom]);

  return (
    <div className={classNames('', className)} {...props}>
      <Modal open={!!eventId} onClose={() => setEventId(null)}>
        {!!eventId && !selectedEvent && (
          <div className="p-4 text-center text-gray-500">
            <Loading size="lg" />
          </div>
        )}
        <EventInModal
          event={events.find((event) => event._id === eventId) as Event}
          onStatusChange={async (status) => {
            if (status === 'registered' && !isParticipating) {
              await participate(selectedEvent?._id as string);
            } else {
              await updateParticipation((myParticipation as EventParticipant)._id, status);
            }
            await refetch();
          }}
        />
      </Modal>
      <Calendar
        loading={loading}
        events={events}
        className="min-h-[80vh]"
        showAllEvents
        components={{
          event: ({ event: originalEvent }) => {
            const event = originalEvent as Event;
            const { startAt: start, tool } = event || {};

            const name = event.name || tool.name;
            const duration = event.duration || tool.duration || 0;

            const myParticipation = event.participants?.find((participant: any) => participant.userId === user?.id);
            const status = myParticipation?.status;

            return (
              <button key={event._id} className="cursor-pointer w-full" onClick={() => setEventId(event._id)}>
                {status === 'registered' ? (
                  <Marquee className="flex items-center gap-2" speed={30}>
                    <span className="font-bold inline-block mr-1">Enregistré</span>
                    {name}
                  </Marquee>
                ) : (
                  <div className="text-left">{name}</div>
                )}

                <small className="flex items-center justify-between gap-2">
                  <span>{dayjs(start).format('HH:mm')}</span>
                  <span className="font-bold">{formatMinutesToHoursAndMinutes(duration)}</span>
                </small>
              </button>
            );
          },
        }}
        dayPropGetter={(date) => {
          const today = dayjs().startOf('day');
          return {
            className: dayjs(date).isBefore(today) && !dayjs(date).isSame(today, 'day') ? 'rbc-past-day bg-gray-100' : '',
          };
        }}
        eventPropGetter={({ tool: { type }, startAt: start }: any) => {
          return {
            className: classNames(getColorAndLabel(type).color, {
              'opacity-50': dayjs(start).isBefore(dayjs()) && !dayjs(start).isSame(dayjs(), 'day'),
            }),
          };
        }}
        startAccessor={(event: any) => new Date(event.startAt)}
        endAccessor={(event: any) => new Date(event.endAt)}
      />
    </div>
  );
};

export default EventCalendar;
